// Strategy to define useState in composables and share around the whole project
// https://nuxt.com/docs/getting-started/state-management#shared-state
import { isValidEmail } from '@workspace/utils/email'

import type { Database } from '~/apis/generated/database'
import { useCurrentLocation } from '~/composables/useCurrentLocation'
import { useCurrentUser } from '~/composables/useCurrentUser'
import { useMfaInfo } from '~/composables/useMfaInfo'
import { useOrganization } from '~/composables/useOrganization'
import { useSupabaseClient } from '~/composables/useSupabaseClient'
import { sendOtp } from '~/repositories/oneTimeTokenRepository'
import { getOrganizationByAccountId } from '~/repositories/organizationRepository'
import { reinviteUser } from '~/repositories/userRepository'

export type UpdateAccountParams = {
  accountId: string
  email?: string
  password?: string
  name?: string
  isPending?: boolean
}

export type SignInReturn = { success: true; isPending: boolean } | { success: false; error: any }

export const useAuth = () => {
  const { refresh: refreshMfaInfo, organizationMfaEnabled, userAccountId, pendingUser } = useMfaInfo()

  // 各ページで共通利用するstateの更新
  // 指定された accountId に紐づく組織・ユーザー情報・現在拠点ID を再取得し、共有stateを更新する。
  // MFAが有効な場合は、OTPの送信および検証が完了してユーザー認証が確立した後に呼び出すこと。
  const refreshMyAffiliations = async (accountId: string) => {
    const { organization } = useOrganization()
    organization.value = await getOrganizationByAccountId(accountId)
    const { currentUser, recoverUser } = useCurrentUser()
    await recoverUser()
    const { currentLocationId } = useCurrentLocation()

    if (!currentUser.value) throw new Error('user not found')
    currentLocationId.value = currentUser.value.locations[0]?.id ?? null
  }

  const signIn = async (email: string, password: string): Promise<SignInReturn> => {
    const supabaseAuthClient = useSupabaseClient<Database>()

    if (!isValidEmail(email)) {
      return { success: false, error: 'invalid email format' }
    }

    const { data: signInData, error: signInError } = await supabaseAuthClient.auth.signInWithPassword({
      email,
      password,
    })
    if (signInError || !signInData?.user) return { success: false, error: signInError || 'authentication failed' }

    try {
      await refreshMfaInfo()

      // MFA有効の場合はOTP送信実行（ただしpendingユーザーの場合はスキップ）
      if (organizationMfaEnabled.value && !pendingUser.value) {
        await sendOtp()
      } else if (!organizationMfaEnabled.value) {
        // MFA無効の場合はstateを更新(MFA有効の場合はこの段階ではrefreshMyAffiliationsのデータロードができない)
        if (!userAccountId.value) return { success: true, isPending: !!pendingUser.value }
        await refreshMyAffiliations(userAccountId.value)
      }
    } catch {
      await signOut().catch(() => undefined) // supabase signin は成功しているのでベストエフォートで signOut しておく
      return { success: false, error: 'failed to refresh user affiliations' }
    }

    return { success: true, isPending: !!pendingUser.value }
  }

  const signOut = async () => {
    const supabaseAuthClient = useSupabaseClient<Database>()
    await supabaseAuthClient.auth.signOut()

    const { organization } = useOrganization()
    organization.value = null
    const { updateCurrentUser } = useCurrentUser()
    updateCurrentUser(null)

    await refreshMfaInfo()
  }

  const recheckSession = async () => {
    // localStorageのsessionは有効期間内でもDB上のsessionが消されている場合、利用できない機能（auth)があるため
    // ログイン画面に飛ばしたい
    // そのため、auth.getUser()を通じてsessionが有効かどうかを確認してsignOut(localStorageのトークンを削除)する
    const { error } = await useSupabaseClient().auth.getUser()
    if (error && error.status === 403) await signOut()
  }

  const reinviteUserAccount = async (userId: number): Promise<{ error: unknown }> => {
    try {
      await reinviteUser(userId)
      return { error: null }
    } catch (error) {
      return { error }
    }
  }

  return {
    signIn,
    signOut,
    recheckSession,
    reinviteUserAccount,
    refreshMyAffiliations,
  }
}
